import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Camera, CrossCircle as RemoveIcon, Attachment } from '@zooplus/icons';
import { FileDescriptor, FileSource } from '../../../services/fileService';
import { ButtonField, FlatBox, FormFieldWrap, GridCol } from '@zooplus/ui-components';
import {
  useFormFieldMainValidationWithOnValueChangeValidation,
  useValidatableFormFieldFocus,
} from '../../../hooks';
import { FileFormFieldValidator } from '../../../hooks/validation/validator/FileFormFieldValidator';
import { ExperimentationContext } from '../../context/ExperimentationContext';
import { isMobile } from 'react-device-detect';

const ALLOWED_MIME_TYPES =
  'image/jpeg,image/jpg,image/pjpeg,image/gif,image/bmp,image/png,image/heic,image/heif,application/pdf';

const AttachmentSection: React.FC = () => {
  const { t } = useTranslation();
  const experimentation = useContext(ExperimentationContext);

  const fileSizeInMb = FileFormFieldValidator.MAX_FILE_SIZE_MB;
  const maxFilesCount = FileFormFieldValidator.MAX_FILES_COUNT;
  const label = t('form.fields.attachments.label');
  const placeholder = t('form.fields.attachments.placeholder', { fileSizeInMb });
  const selectFileButtonLabel = t('form.fields.attachments.select_file');
  const takePictureButtonLabel = t('form.fields.attachments.take_picture');
  const errorMessage = t('form.fields.attachments.error_message', { fileSizeInMb });
  const info = t('form.fields.attachments.info', { maxFilesCount });
  const isRequired = false;
  const shouldSetValidationStateAlways = true;

  const attachmentElementRef = useRef<HTMLInputElement>(null);
  const [attachmentFiles, setAttachmentFiles] = useState([] as Array<FileDescriptor>);
  const inputWrapperElementRef = useRef<HTMLDivElement>(null);
  const [attachingFilesEnabled, setAttachingFilesEnabled] = useState(true);

  const [value, setValue] = useState<FileDescriptor[]>([]);
  const { hasError, validate } = useFormFieldMainValidationWithOnValueChangeValidation(
    new FileFormFieldValidator(isRequired),
    { value },
    shouldSetValidationStateAlways,
  );
  useValidatableFormFieldFocus({
    formFieldSelector: '#attachmentSelectorInput',
    formFieldWrapperElementRef: inputWrapperElementRef,
  });

  const triggerFileUpload = (evt: React.MouseEvent, fileSource: FileSource) => {
    evt.preventDefault();
    setAttachmentFiles(attachmentFiles.concat([new FileDescriptor(fileSource)]));
  };

  useLayoutEffect(() => {
    const mostRecentlyAddedFileDescriptor = attachmentFiles
      .filter((file) => !file.isConfigured())
      .pop();
    const mostRecentlyAddedFileDescriptorIndex = mostRecentlyAddedFileDescriptor?.index;
    const fileInputElement = attachmentElementRef?.current?.querySelector(
      `.fileAttachmentInput-${mostRecentlyAddedFileDescriptorIndex}`,
    ) as HTMLInputElement | null;
    fileInputElement?.click();
  }, [attachmentFiles]);

  useEffect(() => {
    const newValue = attachmentFiles.filter((file) => file.isConfigured());
    setAttachingFilesEnabled(Number(newValue?.length) < maxFilesCount);
    setValue(newValue);
  }, [attachmentFiles]);

  const onChangeCallback = useCallback(
    (fileDescriptorIndex: number, evt: React.ChangeEvent<HTMLInputElement>) => {
      const fileList = evt.target.files;
      if (!fileList?.length || fileList.length > 1) {
        removeAttachmentFileWithIndexValue(fileDescriptorIndex);
        return;
      }

      const fileDescriptorArrayIndex =
        getAttachmentFileArrayIndexWithIndexValue(fileDescriptorIndex);
      const fileDescriptor = attachmentFiles[fileDescriptorArrayIndex];
      Object.keys(fileList)
        .map((key: string) => fileList[Number(key)])
        .forEach((file) => fileDescriptor.useFile(file));

      setAttachmentFiles(
        ([] as FileDescriptor[]).concat(attachmentFiles.filter((file) => file.isConfigured())),
      );
      validate();
    },
    [attachmentFiles],
  );

  const removeAttachmentFileWithIndexValue = (fileDescriptorIndex: number) => {
    const fileDescriptorArrayIndex = getAttachmentFileArrayIndexWithIndexValue(fileDescriptorIndex);
    fileDescriptorArrayIndex >= 0 && attachmentFiles.splice(fileDescriptorArrayIndex, 1);
    setAttachmentFiles(([] as FileDescriptor[]).concat(attachmentFiles));
  };

  const getAttachmentFileArrayIndexWithIndexValue = (fileDescriptorIndex: number): number => {
    return attachmentFiles.findIndex((file: FileDescriptor) => file.index === fileDescriptorIndex);
  };

  const validateCallback = useCallback(() => validate(), [validate]);

  const onRemoveCallback = useCallback(
    (file: FileDescriptor) => {
      removeAttachmentFileWithIndexValue(file.index);
    },
    [attachmentFiles],
  );

  function oldVersion() {
    return (
      <div className="contactForm__attachment-input">
        <ButtonField
          buttonText={selectFileButtonLabel}
          inputProps={{
            id: 'attachmentSelectorInput',
            type: 'text',
            readOnly: true,
          }}
          onChange={function noRefCheck() {}}
          isDisabled={!attachingFilesEnabled}
          placeholder={placeholder}
          onClick={(e) => triggerFileUpload(e, 'GENERIC')}
          onButtonClick={(e) => triggerFileUpload(e, 'GENERIC')}
          onBlur={validateCallback}
        />
      </div>
    );
  }

  function newVersion() {
    return (
      <div className="contactForm__attachmentV2-input">
        <button
          id="attachmentSelectorButton"
          aria-label="Choose attachment file"
          title="Select attachment file"
          className="v3-btn v3-btn--supplementary"
          onClick={(e) => triggerFileUpload(e, 'GENERIC')}
          disabled={!attachingFilesEnabled}>
          <Attachment className={'attachment-icon'} aria-label="attachment" />
          {selectFileButtonLabel}
        </button>
        {isMobile && (
          <button
            id="attachmentImageSelectorButton"
            aria-label="Take a picture"
            title="Take a picture"
            className="v3-btn v3-btn--supplementary"
            onClick={(e) => triggerFileUpload(e, 'IMAGE')}
            disabled={!attachingFilesEnabled}>
            <Camera className={'attachment-icon'} aria-label="attachment" />
            {takePictureButtonLabel}
          </button>
        )}
      </div>
    );
  }

  return (
    <div
      data-test="contactForm.field.attachment"
      data-form-control-type="FILE"
      className="contactForm__field"
      ref={inputWrapperElementRef}>
      <FormFieldWrap
        labelFor="attachment"
        isRequired={isRequired}
        labelText={label}
        infoMessage={info}
        errorMessage={hasError && errorMessage}
        ref={inputWrapperElementRef}
        data-test="contactForm.field.attachmentManager">
        {experimentation.isTakeAPictureDisplayed() ? newVersion() : oldVersion()}
        <div ref={attachmentElementRef} data-test="contactForm.field.attachmentsInputs">
          {attachmentFiles.map(({ index, fileSource }) => (
            <input
              key={index}
              name="attachments"
              className={`fileAttachmentInput fileAttachmentInput-${index}`}
              type="file"
              onChange={(evt) => onChangeCallback(index, evt)}
              onBlur={validateCallback}
              {...(fileSource === 'GENERIC' && { accept: ALLOWED_MIME_TYPES })}
              {...(fileSource === 'IMAGE' && { accept: 'image/*', capture: 'environment' })}
            />
          ))}
        </div>
      </FormFieldWrap>

      <AttachmentList attachmentFiles={attachmentFiles} onRemove={onRemoveCallback} />
    </div>
  );
};

export default AttachmentSection;

const AttachmentList: React.FC<
  Object & { attachmentFiles: FileDescriptor[]; onRemove: (file: FileDescriptor) => any }
> = ({ attachmentFiles, onRemove }) => {
  const configuredAttachments = attachmentFiles.filter((file) => file.isConfigured());

  const attachmentContent = (file: FileDescriptor) => (
    <FlatBox variant="white" className="attachmentList__content" key={file.index}>
      <div className="attachmentList__fileName pp-mr-xs">{file.fileName}</div>
      <div className="attachmentList__fileSizeAndActions">
        <div className="attachmentList__fileSize pp-mr-xs">
          <span className="attachmentList__fileSizeValue">{file.fileSize!!.value}</span>{' '}
          <span className="attachmentList__fileSizeUnit">{file.fileSize!!.unit}</span>
        </div>
        <div className="attachmentList__removeButton" onClick={() => onRemove(file)}>
          <RemoveIcon fill="inherit" />
        </div>
      </div>
    </FlatBox>
  );

  return (
    <>
      {!!configuredAttachments.length && (
        <GridCol
          colSpan={12}
          rowStart={4}
          start={1}
          md={{ colSpan: 7, start: 6, rowStart: 3 }}
          className="attachmentList pp-my-xs"
          data-test="contactForm.field.attachmentList">
          {configuredAttachments.map((file: FileDescriptor) => attachmentContent(file))}
        </GridCol>
      )}
    </>
  );
};
